import { useMemo } from "react";
import { F } from "../utils/gerencialFields";
import { startOfDay, toDate } from "../utils/status";
import {
  CAPACIDADE_DEFAULT,
  detectarGargalo,
} from "../utils/toc/bottleneckDetector";
import {
  ambientesCriticos,
  contarPorZona,
  histogramaConsumo,
  statusPulmao,
} from "../utils/toc/bufferManager";
import { ORDEM_ETAPAS, getEtapaAtual } from "../utils/toc/etapas";
import { PLAYBOOKS } from "../utils/toc/playbooks";
import {
  calcularLeadTimesMedios,
  proximasLiberacoes,
} from "../utils/toc/ropeScheduler";
import {
  burndownDoMes,
  calcularThroughputMes,
  historicoMensalThroughput,
} from "../utils/toc/throughputCalculator";
import { consolidarTOC, montarOE } from "../utils/toc/tocAccountingBridge";
import {
  calcularWIP,
  totalWIP,
} from "../utils/toc/wipCalculator";

function emCompras(amb) {
  return getEtapaAtual(amb) === "COMPRAS";
}

function isAmbienteEntregue(amb) {
  // CARREGAMENTO (CN) é a data real de saída da fábrica.
  // ENTREGA_CLIENTE (CO) é fórmula de previsão e não pode ser usada aqui.
  return Boolean(toDate(amb?.[F.CARREGAMENTO]));
}

const TAMBOR_DIAS = 10;

export function useTOC(ambientes, parametros, { hojeOverride } = {}) {
  const hoje = useMemo(
    () => startOfDay(hojeOverride || new Date()) || new Date(),
    [hojeOverride]
  );

  const lista = useMemo(
    () => (Array.isArray(ambientes) ? ambientes : []),
    [ambientes]
  );

  const wipPorEtapa = useMemo(
    () => calcularWIP(lista, hoje),
    [lista, hoje]
  );

  const totalWipResumo = useMemo(
    () => totalWIP(wipPorEtapa),
    [wipPorEtapa]
  );

  const ambientesEmWip = useMemo(
    () => lista.filter((amb) => getEtapaAtual(amb)),
    [lista]
  );

  const contagemPorZona = useMemo(
    () => contarPorZona(ambientesEmWip, hoje, parametros.zonasPulmao),
    [ambientesEmWip, hoje, parametros.zonasPulmao]
  );

  const criticos = useMemo(
    () =>
      ambientesCriticos(ambientesEmWip, {
        hoje,
        zonas: parametros.zonasPulmao,
        limite: 10,
      }),
    [ambientesEmWip, hoje, parametros.zonasPulmao]
  );

  const detec = useMemo(
    () =>
      // Sem `capacidades`: todas as etapas com a capacidade padrão, então a
      // ordem é a da carga ("Etapa com maior carga" no card). A capacidade
      // medida da fábrica está em /legado/capacidade.
      detectarGargalo(lista, {
        hoje,
        zonasPulmao: parametros.zonasPulmao,
      }),
    [lista, hoje, parametros.zonasPulmao]
  );

  const ccrEtapa = parametros.ccrEtapa || detec.gargalo?.etapa || null;

  const throughput = useMemo(
    () =>
      calcularThroughputMes(lista, {
        hoje,
        pctMateriais: parametros.pctMateriaisMedio,
        metaGanhoRS: parametros.metaGanhoMesRS,
        metaAmbientes: parametros.metaAmbientesMes,
      }),
    [
      lista,
      hoje,
      parametros.pctMateriaisMedio,
      parametros.metaGanhoMesRS,
      parametros.metaAmbientesMes,
    ]
  );

  const burndown = useMemo(
    () => burndownDoMes(lista, { hoje }),
    [lista, hoje]
  );

  const leadTimesMedios = useMemo(
    () => calcularLeadTimesMedios(lista.filter(isAmbienteEntregue)),
    [lista]
  );

  const tambor = useMemo(() => {
    if (!ccrEtapa) return [];
    const idxCcr = ORDEM_ETAPAS.indexOf(ccrEtapa);
    const candidatos = [];
    for (const amb of lista) {
      const atual = getEtapaAtual(amb);
      if (!atual) continue;
      const idxAtual = ORDEM_ETAPAS.indexOf(atual);
      if (idxAtual !== idxCcr && idxAtual !== idxCcr - 1) continue;
      const status = statusPulmao(amb, hoje, parametros.zonasPulmao);
      candidatos.push({ amb, status, etapaAtual: atual });
    }
    candidatos.sort(
      (a, b) => (b.status.pctConsumido ?? 0) - (a.status.pctConsumido ?? 0)
    );
    return candidatos.slice(0, Math.ceil(CAPACIDADE_DEFAULT * TAMBOR_DIAS));
  }, [lista, hoje, ccrEtapa, parametros.zonasPulmao]);

  const pulmaoHist = useMemo(
    () =>
      histogramaConsumo(ambientesEmWip, {
        hoje,
        zonas: parametros.zonasPulmao,
        bins: 10,
      }),
    [ambientesEmWip, hoje, parametros.zonasPulmao]
  );

  const historicoT = useMemo(
    () =>
      historicoMensalThroughput(lista, {
        hoje,
        meses: 6,
        pctMateriais: parametros.pctMateriaisMedio,
      }),
    [lista, hoje, parametros.pctMateriaisMedio]
  );

  const proximas = useMemo(() => {
    if (!ccrEtapa) return [];
    return proximasLiberacoes(lista.filter(emCompras), {
      hoje,
      ccrEtapa,
      leadTimesMedios,
      limite: 10,
    });
  }, [lista, hoje, ccrEtapa, leadTimesMedios]);

  const oeResumo = useMemo(
    () => montarOE(parametros.oeMensal),
    [parametros.oeMensal]
  );

  const contabil = useMemo(
    () =>
      consolidarTOC({
        throughput,
        wipPorEtapa,
        oeMensal: oeResumo.total,
      }),
    [throughput, wipPorEtapa, oeResumo.total]
  );

  const playbookDoGargalo = useMemo(() => {
    const etapa = detec.gargalo?.etapa;
    if (!etapa) return null;
    return { etapa, playbook: PLAYBOOKS[etapa] || null };
  }, [detec.gargalo]);

  const snapshotGargalo = useMemo(() => {
    if (!detec.gargalo) return null;
    return {
      etapa: detec.gargalo.etapa,
      utilizacao: detec.gargalo.utilizacao,
      wipTotal: totalWipResumo.count,
      valorWipRS: totalWipResumo.valorRS,
      buffersVermelhos: contagemPorZona.vermelho,
      buffersPretos: contagemPorZona.preto,
      throughputAcumuladoMes: throughput.ganho,
    };
  }, [detec.gargalo, totalWipResumo, contagemPorZona, throughput.ganho]);

  return {
    hoje,
    wipPorEtapa,
    totalWip: totalWipResumo,
    contagemPorZona,
    criticos,
    gargalo: detec.gargalo,
    rankingEtapas: detec.ranking,
    janelaDiasUteis: detec.janelaDiasUteis,
    ccrEtapa,
    throughput,
    burndown,
    leadTimesMedios,
    proximasLiberacoes: proximas,
    tambor,
    pulmaoHist,
    historicoT,
    tamborDias: TAMBOR_DIAS,
    oeResumo,
    contabil,
    playbookDoGargalo,
    snapshotGargalo,
  };
}
